import { CustomDate, deepMap } from '@nemuru-eng/js-api-client';

import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { OnboardingDetailsRepository } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/entities/OnboardingDetailsRepository';
import { BorrowerOnboardingDetailsDto } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/types/BorrowerOnboardingDetailsDto';
import { convertDatesToLocalDateString } from '@/utils/convertDatesToLocalDateString';

export class OnboardingDetailsLocalStorageRepository implements OnboardingDetailsRepository {
  private readonly storageKey = 'onboarding_details_repository';

  private getAll(): Record<string, StoredBorrowerOnboardingDetails[]> {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (!data) return {};
      return JSON.parse(data);
    } catch (err) {
      if (err instanceof DOMException) {
        console.warn('Local storage read-access restricted');
        console.warn('Failed to getAll');
        return {};
      }
      throw err;
    }
  }

  public get(agentId: string): StoredBorrowerOnboardingDetails[] {
    const data = this.getAll();

    const mappedData = Object.keys(data)
      .filter((key) => key === agentId)
      .flatMap((key) =>
        data[key].map((onboardingDetails) => ({
          ...onboardingDetails,
        })),
      );

    const mappedDataWithDates = this.castDates(mappedData);

    return this.filterExistingOnboardingDetails(mappedDataWithDates);
  }

  public set(agentId: string, onboardingDetails: BorrowerOnboardingDetailsDto[]): void {
    const data = this.getAll();

    const existingOnboardingDetails = data?.[agentId] ?? [];

    const filteredOnboardingDetails = this.filterExistingOnboardingDetails(existingOnboardingDetails);

    localStorage.setItem(
      this.storageKey,
      JSON.stringify({
        ...(data ?? {}),
        [agentId]: [...filteredOnboardingDetails, ...convertDatesToLocalDateString(onboardingDetails, ['createdAt'])],
      }),
    );
  }

  private filterExistingOnboardingDetails(
    onboardingDetails: StoredBorrowerOnboardingDetails[],
  ): StoredBorrowerOnboardingDetails[] {
    return onboardingDetails.reduce<StoredBorrowerOnboardingDetails[]>((acc, current) => {
      return acc.find(
        (item) =>
          item.email === current.email &&
          item.phoneNumber === current.phoneNumber &&
          item.userDetails.documentId.documentNumber === current.userDetails.documentId.documentNumber,
      )
        ? acc
        : [...acc, current];
    }, []);
  }

  public remove(id: string, agentId: string): void {
    const data = this.getAll();

    const onboardingDetails = data[agentId];

    if (!onboardingDetails) {
      return;
    }

    const filteredOnboardingDetails = onboardingDetails.filter((details) => details.id !== id);

    localStorage.setItem(this.storageKey, JSON.stringify({ ...data, [agentId]: filteredOnboardingDetails }));
  }

  public castDates(onboardingDetails: StoredBorrowerOnboardingDetails[]): StoredBorrowerOnboardingDetails[] {
    return deepMap(
      onboardingDetails,
      (key) => key,
      (value) => {
        if (CustomDate.isLocalDateString(value) || CustomDate.isLocalDateTimeString(value)) {
          return new CustomDate(value);
        }
        return value;
      },
    );
  }
}
